import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import { ApiError, getLearningStatus, invalidateLearningAttention, invalidateLearningConfigs, LearningStatus, Plan, TargetDetail, TargetType } from '../services/learningApi';
import { useAuth } from '../contexts/AuthContext';
import { useApis } from '../components/learning/common';
import { PlanSummary } from '../components/learning/PlanSummary';
import { OverviewTab } from '../components/learning/OverviewTab';
import { ResultsTab } from '../components/learning/ResultsTab';
import { KnowledgeTab } from '../components/learning/KnowledgeTab';
import { SuggestionsTab } from '../components/learning/SuggestionsTab';
import { HistoryTab } from '../components/learning/CycleViews';
import { ProfileTab } from '../components/learning/ProfileTab';
import { LearningSettingsTab } from '../components/learning/LearningSettingsTab';

const TABS = ['overview', 'results', 'knowledge', 'suggestions', 'history', 'profile', 'settings'] as const;
type TabKey = (typeof TABS)[number];

export const SelfImprovement: React.FC = () => {
  const { targetType: rawType, targetId = '' } = useParams();
  const targetType: TargetType = rawType === 'chain' ? 'chain' : 'function';
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: TabKey = (TABS as readonly string[]).includes(searchParams.get('tab') || '') ? (searchParams.get('tab') as TabKey) : 'overview';
  const navigate = useNavigate();
  const { getIdToken } = useAuth();
  const apis = useApis();

  const [status, setStatus] = useState<LearningStatus | null>(null);
  const [detail, setDetail] = useState<TargetDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [toggling, setToggling] = useState(false);
  const [runDialog, setRunDialog] = useState<{ plan: Plan | null; loading: boolean; starting: boolean; error?: string } | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const d = await apis.learning.target(targetType, targetId);
      invalidateLearningAttention();
      invalidateLearningConfigs();
      setDetail(d);
      setError(null);
      setRefreshKey((k) => k + 1);
    } catch (e: any) {
      setError(e instanceof ApiError && e.status === 403 ? null : e.message);
    }
  }, [apis, targetType, targetId]);

  useEffect(() => {
    getLearningStatus(getIdToken, true).then(setStatus);
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  const activeCycleId = detail?.active_cycle?.id;
  useEffect(() => {
    if (!activeCycleId || tab === 'overview') return;
    const timer = setInterval(refresh, 10000);
    return () => clearInterval(timer);
  }, [activeCycleId, tab, refresh]);

  const setTab = (t: string) => setSearchParams(t === 'overview' ? {} : { tab: t }, { replace: true });

  const toggleEnabled = async (enabled: boolean) => {
    setToggling(true);
    try {
      await apis.learning.updateTarget(targetType, targetId, { enabled });
      setNotice(enabled ? (detail?.settings.schedule.mode === 'manual' ? 'Turned on. Run an analysis whenever you like, or set a schedule under Settings.' : 'Turned on. Analyses will run on the schedule in Settings.') : 'Turned off. Learned knowledge is no longer used, but nothing is deleted.');
      await refresh();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setToggling(false);
    }
  };

  const openRunDialog = async () => {
    setRunDialog({ plan: null, loading: true, starting: false });
    try {
      const plan = await apis.learning.preview(targetType, targetId);
      setRunDialog({ plan, loading: false, starting: false });
    } catch (e: any) {
      setRunDialog({ plan: null, loading: false, starting: false, error: e.message });
    }
  };

  const startRun = async () => {
    if (!runDialog) return;
    setRunDialog({ ...runDialog, starting: true });
    try {
      await apis.learning.startCycle(targetType, targetId);
      setRunDialog(null);
      setTab('overview');
      await refresh();
    } catch (e: any) {
      setRunDialog({ ...runDialog, starting: false, error: e.message });
    }
  };

  if (status && !status.enabled) {
    return (
      <Box sx={{ maxWidth: 720 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          Back
        </Button>
        <Alert severity="info">Self-improvement isn't available on your account yet. Contact us if you'd like early access.</Alert>
      </Box>
    );
  }
  if (!detail || !status) {
    return error ? <Alert severity="error">{error}</Alert> : <CircularProgress />;
  }

  const config = detail.config;
  const enabled = !!config?.enabled;
  const running = !!detail.active_cycle;
  const blocked = status.blocked_reason || (status.paused ? 'Self-improvement is paused platform-wide by an administrator.' : null);
  const versionLabel = detail.target.type === 'function' && detail.target.version ? `v${detail.target.version}` : null;

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          <IconButton onClick={() => navigate(-1)} aria-label="Back">
            <ArrowBackIcon />
          </IconButton>
          <TipsAndUpdatesIcon color="primary" />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" noWrap>
              {detail.target.name}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Self-improvement · {targetType === 'chain' ? 'Chain' : 'AI function'}
              </Typography>
              {versionLabel && <Chip size="small" label={versionLabel} variant="outlined" />}
              {running && <Chip size="small" color="info" label="analyzing…" />}
            </Stack>
          </Box>
        </Stack>
        <Tooltip
          title={
            blocked ||
            (enabled
              ? 'On: analyses can run and in-use knowledge is given to runs. Turning it off stops both; nothing is deleted.'
              : 'Off: no analyses run and learned knowledge is not used.')
          }
        >
          <FormControlLabel
            control={<Switch checked={enabled} disabled={toggling || (!!status.blocked_reason && !enabled)} onChange={(e) => toggleEnabled(e.target.checked)} />}
            label={enabled ? 'On' : 'Off'}
          />
        </Tooltip>
        <Tooltip title={!enabled ? 'Turn it on first' : running ? 'An analysis is already running' : blocked || ''}>
          <span>
            <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={openRunDialog} disabled={!enabled || running || !!blocked}>
              Run analysis
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {blocked && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {blocked}
        </Alert>
      )}
      {detail.warnings.map((w) => (
        <Alert key={w} severity="warning" sx={{ mb: 2 }}>
          {w}
        </Alert>
      ))}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }} variant="scrollable" allowScrollButtonsMobile>
        <Tab value="overview" label={<TabLabel text="Overview" help="Status, latest analysis, and what needs your attention" />} />
        <Tab value="results" label={<TabLabel text="Results" help="Usage and ratings over time, per version and before/after each change you applied" />} />
        <Tab
          value="knowledge"
          label={<TabLabel text="Knowledge" help="What has been learned, what is in use, and the knowledge base it is published to" count={config?.entry_counts?.candidate} countHelp="learnings waiting" />}
        />
        <Tab
          value="suggestions"
          label={<TabLabel text="Suggestions" help="Proposed changes to instructions and settings. Nothing changes until you apply one." count={config?.open_suggestions} countHelp="open suggestions" />}
        />
        <Tab value="history" label={<TabLabel text="History" help="Every analysis: its report, the runs it looked at, cost, and log" />} />
        <Tab value="profile" label={<TabLabel text="Profile" help="How the analyzer understands this target: what good looks like and how to generalize" />} />
        <Tab value="settings" label={<TabLabel text="Settings" help="What to learn, when to run, budget, and advanced options" />} />
      </Tabs>

      {tab === 'overview' && <OverviewTab targetType={targetType} targetId={targetId} detail={detail} status={status} onTab={setTab} onChanged={refresh} />}
      {tab === 'results' && <ResultsTab targetType={targetType} targetId={targetId} refreshKey={refreshKey} />}
      {tab === 'knowledge' && <KnowledgeTab targetType={targetType} targetId={targetId} detail={detail} onChanged={refresh} />}
      {tab === 'suggestions' && <SuggestionsTab targetType={targetType} targetId={targetId} detail={detail} onChanged={refresh} />}
      {tab === 'history' && <HistoryTab targetType={targetType} targetId={targetId} hasConfig={!!config} refreshKey={refreshKey} />}
      {tab === 'profile' && <ProfileTab targetType={targetType} targetId={targetId} detail={detail} onChanged={refresh} />}
      {tab === 'settings' && <LearningSettingsTab targetType={targetType} targetId={targetId} detail={detail} status={status} onSaved={refresh} />}

      <Dialog open={!!runDialog} onClose={() => !runDialog?.starting && setRunDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Run analysis now?</DialogTitle>
        <DialogContent>
          {runDialog?.loading && <CircularProgress size={24} />}
          {runDialog?.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {runDialog.error}
            </Alert>
          )}
          {runDialog?.plan && <PlanSummary plan={runDialog.plan} />}
          {runDialog?.plan && config?.profile_status === 'none' && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              A learning profile will be generated first (included in the estimate).
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRunDialog(null)} disabled={runDialog?.starting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={startRun} disabled={!runDialog?.plan?.can_run || !!runDialog?.plan?.cap_error || runDialog?.starting}>
            {runDialog?.starting ? 'Starting…' : 'Start'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const TabLabel: React.FC<{ text: string; help: string; count?: number; countHelp?: string }> = ({ text, help, count, countHelp }) => (
  <Tooltip title={help} enterDelay={500}>
    <Stack direction="row" spacing={0.75} alignItems="center">
      <span>{text}</span>
      {!!count && (
        <Chip size="small" color="primary" label={count} title={`${count} ${countHelp || ''}`.trim()} sx={{ height: 18, fontSize: 11, '& .MuiChip-label': { px: 0.75 } }} />
      )}
    </Stack>
  </Tooltip>
);

export default SelfImprovement;
